import {
  forkJoin as observableForkJoin,
  combineLatest as observableCombineLatest,
  of as observableOf,
  Observable,
} from 'rxjs';

import {first, map, mergeMap, takeWhile, catchError} from 'rxjs/operators';
import {AbstractControlComponent} from './abstract-control';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  ViewChild,
} from '@angular/core';
import {SearchScopeConfiguration} from '../../../cache/uiconfiguration/searchscopeconfiguration';
import {
  SmdDataSelectionMode,
  SmdDataTable,
} from '../../../shared/component/smd-datatable/datatable.component';
import {ValuesObject} from '../detail-tabconfig';
import {ActionBarConfiguration} from '../../../cache/uiconfiguration/actionbarconfiguration';
import {Router} from '@angular/router';
import {ViewTabComponent} from '../viewtab.component';
import {AddViewEvent} from '../../events/addviewevent';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {MultivalueDialogComponent} from './multivalue-dialog/multivalue-dialog.component';
import {MultivalueDialogResult} from './multivalue-dialog/multivalue-dialog-result';
import {CacheService} from '../../../cache/cache.service';
import {LanguageService} from '../../../language/language.service';
import {ListviewActionButton} from '../../../shared/component/smd-datatable/datatable-header.component';
import {DialogConfiguration} from '../../../cache/uiconfiguration/dialogconfiguration';
import {SmdDataRowModel} from '../../../shared/component/smd-datatable/datatable-row.component';
import {Resource} from '../../resource';
import {SmdDataRowMark} from '../../../shared/component/index';
import {multivalueRequired} from './validators/multivalue-required-validator';
import {ErrorService} from '../../../error/error.service';
import {EnvironmentConfigService} from '../../../core/services/environment-config.service';

@Component({
  selector: 'ctrl-listview',
  template: `
    @if (listViewTitle || ((getCaughtObservable(isEnabled) | async) === true && enableManage)) {
      <div class="listview-header">
        <div>
          <h2>{{ getCaughtObservable(listViewTitle) | async }}</h2>
          @if (
            (getCaughtObservable(isEnabled) | async) &&
            formControl.touched &&
            formControl.hasError('required')
          ) {
            <mat-error>
              <language
                key="ControlErrorRequired"
                defaultValue="This field is required."
              ></language>
            </mat-error>
          }
        </div>
        @if ((getCaughtObservable(isEnabled) | async) && enableManage) {
          <button mat-raised-button color="primary" (click)="onManageClick()">
            <mat-icon>tune</mat-icon>
            <language key="CtrlListViewManage" defaultValue="manage"></language>
          </button>
        }
      </div>
    }
    <smd-datatable
      [hidden]="!showList"
      #list
      [paginated]="true"
      (onAddView)="_parent.onAddView.emit($event)"
      [defaultPageSize]="getCaughtObservable(_config?.getPropertyValues('PageSize')) | async"
      [responsive]="true"
      (onError)="handleError($event)"
      [emptyResultText]="getCaughtObservable(_config?.getPropertyValues('EmptyResultText')) | async"
      [selectionMode]="listSelectionMode"
      [mergeRemoteAndLocal]="true"
    >
      <smd-datatable-header
        [actionBarConfigs]="listActionBarConfigs"
        (onAction)="actionBarClick($event)"
      ></smd-datatable-header>
    </smd-datatable>
    @if (!showList) {
      <p>This list type is not supported yet.</p>
    }
  `,
  styles: [
    '.listview-header { display: flex; flex-direction: row; align-items: center; justify-content: space-between; padding-bottom: 10px; } .listview-header h2 { flex: 1 1 auto; margin: 0; font-size: 24px; line-height: 47px; } .listview-header button { text-transform: uppercase; } smd-datatable ::ng-deep mat-card-header { display: none; }',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlListviewComponent extends AbstractControlComponent {
  constructor(
    _parent: ViewTabComponent,
    private dialog: MatDialog,
    private cacheService: CacheService,
    changeDetector: ChangeDetectorRef,
    private router: Router,
    _ngZone: NgZone,
    errorService: ErrorService,
    private envConfig: EnvironmentConfigService,
  ) {
    super(_parent, 'SelectedValue', 'SelectedValue', changeDetector, _ngZone, errorService);
  }

  private _objectType: string = null;
  private _objectID: string = null;
  @Input() objectDisplayName: string = null;

  enableManage: boolean = true;

  @Input()
  set objectType(objectType: string) {
    this._objectType = objectType;
    this.initListUpdate();
  }

  get objectType(): string {
    return this._objectType;
  }

  @Input()
  set objectID(objectID: string) {
    this._objectID = objectID;
    this.initListUpdate();
  }

  get objectID(): string {
    return this._objectID;
  }

  @ViewChild('list', {static: true}) private listDataTable: SmdDataTable;
  showList: boolean = true;
  listViewTitle: Observable<string> = null;
  listActionBarConfigs: ActionBarConfiguration[] = null;
  listSelectionMode: SmdDataSelectionMode = SmdDataSelectionMode.Multiple;
  private resObjTypeObs;
  private columnsObs;

  protected initValidators(): void {
    if (this._config == null || this.formControl == null) {
      throw Error('Initialized target too early.');
    }

    this._config
      .getBooleanPropertyValues('Required')
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
      )
      .subscribe(required => {
        if (required === true) {
          this.formControl.setValidators([multivalueRequired()]);
        } else {
          this.formControl.clearValidators();
        }
        this.formControl.updateValueAndValidity({emitEvent: false});
      });

    this.resObjTypeObs = this._config.getPropertyValues('ResultObjectType');
    this.columnsObs = this._config.getPropertyValues('ColumnsToDisplay');

    if (this._config.propertyExists('ListViewTitle')) {
      this.listViewTitle = this._config.getPropertyValues('ListViewTitle');
    } else if (this._config.propertyExists('ShowTitleBar')) {
      this.listViewTitle = this._config.getBooleanPropertyValues('ShowTitleBar').pipe(
        mergeMap((show: boolean) => {
          if (show === true) {
            return this._config.Caption.pipe(map(vo => (vo ? vo.Values : null)));
          } else {
            return observableOf(null);
          }
        }),
      );
    } else {
      this.listViewTitle = null;
    }

    this.initListUpdate();
  }

  protected formatSource(source: any): any {
    return source;
  }

  protected formatTarget(value: any): any {
    return value;
  }

  private initListUpdate(): void {
    if (this._objectType == null) return;
    if (
      this.sourceVoObs != null &&
      this._objectType != null &&
      this._objectID != null &&
      this.resObjTypeObs != null &&
      this.columnsObs != null
    ) {
      observableCombineLatest(
        this.sourceVoObs,
        this.resObjTypeObs,
        this.columnsObs,
        (source: ValuesObject<any>, resObjType: string, columns: string) => {
          if (resObjType == null) {
            resObjType = this.envConfig.getObjectTypeName('Resource');
          }

          // /ResultObjectType[ObjectID=/ResourceObjectType[ObjectID='ResourceObjectID']/Attribute]

          const xpath: string =
            '/' +
            resObjType +
            '[' +
            this.envConfig.getAttributeName('ObjectID') +
            '=/' +
            this._objectType +
            '[' +
            this.envConfig.getAttributeName('ObjectID') +
            "='" +
            this._objectID +
            "']/" +
            source.BindingPath +
            ']';
          const ssc: SearchScopeConfiguration = <SearchScopeConfiguration>{
            id: null,
            displayname: 'All',
            xpath: xpath,
            resultobjecttype: resObjType,
            attributedisplayed: columns.replace(/,/g, ';'),
          };
          this.enableManage = true;
          this.showList = true;
          this.listDataTable.localColumns = columns.split(',').map(c => c.trim());
          this.listDataTable.localResultObjectType = ssc.resultobjecttype;

          if (
            source.SpecialObject != null &&
            source.SpecialObject instanceof MultivalueDialogResult
          ) {
            const localAddedRMs: SmdDataRowModel[] = [];
            this.listDataTable.localRowModels = localAddedRMs;
            this.listDataTable.remoteSpecialMark = SmdDataRowMark.HideRow;

            this.listDataTable.remoteSpecialMarkedObjectIDs = source.SpecialObject.Changes.filter(
              rm => rm.mark === SmdDataRowMark.Remove,
            ).map(rm => rm.model[this.envConfig.getAttributeName('ObjectID')]);

            source.SpecialObject.Changes.forEach((row: SmdDataRowModel) => {
              if (row.mark === SmdDataRowMark.Add) {
                localAddedRMs.push(new SmdDataRowModel(row.model));
              }
            });
          } else {
            this.listDataTable.remoteSpecialMarkedObjectIDs = null;
            this.listDataTable.localRowModels = null;
          }
          this.listDataTable.reloadList([ssc]);
        },
      )
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          takeWhile(() => this.alive),
        )
        .subscribe();
    } else if (this._config.propertyExists('ListFilter')) {
      const listFilterObs = this._config.getPropertyValues('ListFilter');
      if (this._config.propertyExists('ActionBarKeywords')) {
        const actionBarKeywordsObs = this._config.getPropertyValues('ActionBarKeywords');
        actionBarKeywordsObs
          .pipe(
            map((strVal: string) => strVal.split(',')),
            catchError((error, caught) => this.handleError(error, caught)),
            first(),
          )
          .subscribe((keywords: string[]) => {
            const sscsObs: Observable<SearchScopeConfiguration[]>[] = [];
            keywords.forEach((keyword: string) => {
              sscsObs.push(this.cacheService.getSearchScopeConfigurationForObjectType(keyword));
            });

            observableForkJoin(sscsObs)
              .pipe(
                map((sscss: SearchScopeConfiguration[][]) => {
                  return [].concat.apply([], sscss);
                }),
                map((searchscopes: SearchScopeConfiguration[]) => {
                  let newscopes = [];
                  searchscopes.forEach((searchscope: SearchScopeConfiguration) => {
                    let scope: SearchScopeConfiguration = {
                      id: searchscope.id,
                      displayname: searchscope.displayname,
                      description: searchscope.description,
                      usagekeyword: searchscope.usagekeyword,
                      order: searchscope.order,
                      attributesearched: searchscope.attributesearched,
                      xpath: searchscope.xpath,
                      resultobjecttype: searchscope.resultobjecttype,
                      attributedisplayed: searchscope.attributedisplayed,
                      sortingattribute: searchscope.sortingattribute,
                      sortdirection: searchscope.sortdirection,
                    };

                    if (scope.xpath.includes('%ObjectID%')) {
                      if (this._objectID !== null) {
                        scope.xpath = scope.xpath.replace(/%ObjectID%/g, this._objectID);
                      }
                    }
                    newscopes.push(scope);
                  });
                  return newscopes;
                }),
              )
              .subscribe((searchscopes: SearchScopeConfiguration[]) => {
                this.enableManage = false;
                this.showList = true;
                this.listDataTable.reloadList(searchscopes);
              });
          });
      } else {
        observableCombineLatest(
          listFilterObs,
          this.resObjTypeObs,
          this.columnsObs,
          (filter: string, resObjType: string, columns: string) => {
            if (resObjType == null) {
              resObjType = this.envConfig.getObjectTypeName('Resource');
            }

            let searchscopes = [];
            const ssc: SearchScopeConfiguration = <SearchScopeConfiguration>{
              id: null,
              displayname: 'All',
              xpath: filter,
              resultobjecttype: resObjType,
              attributedisplayed: columns.replace(/,/g, ';'),
            };
            searchscopes.push(ssc);

            this.enableManage = false;
            this.showList = true;
            this.listDataTable.reloadList(searchscopes);
          },
        )
          .pipe(
            catchError((error, caught) => this.handleError(error, caught)),
            takeWhile(() => this.alive),
          )
          .subscribe();
      }
    }

    if (
      this._config.propertyExists('EnableSelection') &&
      this._config.propertyExists('SingleSelection')
    ) {
      observableCombineLatest(
        this._config.getBooleanPropertyValues('EnableSelection'),
        this._config.getBooleanPropertyValues('SingleSelection'),
        (enable: boolean, single: boolean) => {
          if (!enable) {
            this.listSelectionMode = SmdDataSelectionMode.Disable;
          } else if (single) {
            this.listSelectionMode = SmdDataSelectionMode.Single;
          } else {
            this.listSelectionMode = SmdDataSelectionMode.Multiple;
          }
        },
      );
    } else if (this._config.propertyExists('EnableSelection')) {
      this._config
        .getBooleanPropertyValues('EnableSelection')
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          takeWhile(() => this.alive),
        )
        .subscribe((enable: boolean) => {
          if (enable) {
            this.listSelectionMode = SmdDataSelectionMode.Multiple;
          } else {
            this.listSelectionMode = SmdDataSelectionMode.Disable;
          }
        });
    } else if (this._config.propertyExists('SingleSelection')) {
      this._config
        .getBooleanPropertyValues('SingleSelection')
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          takeWhile(() => this.alive),
        )
        .subscribe((single: boolean) => {
          if (single) {
            this.listSelectionMode = SmdDataSelectionMode.Single;
          } else {
            this.listSelectionMode = SmdDataSelectionMode.Multiple;
          }
        });
    }

    if (this._config.propertyExists('ShowRefreshButton')) {
      this._config
        .getBooleanPropertyValues('ShowRefreshButton')
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe((value: boolean) => {
          if (value) {
            const tooltip = this.cacheService
              .getLanguageResource()
              .pipe(map(lr => LanguageService.translate('Refresh', 'Refresh', lr)));
            this.listDataTable.header.listviewActionButtons = [
              new ListviewActionButton('refresh', tooltip, () => this.listDataTable.refreshData()),
            ];
          } else {
            this.listDataTable.header.listviewActionButtons = null;
          }
        });
    } else {
      this.listDataTable.header.listviewActionButtons = null;
    }

    if (this._config.propertyExists('ActionBarResources')) {
      this._config
        .getPropertyValues('ActionBarResources')
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe((keywordsStr: any) => {
          if (keywordsStr == null || !(typeof keywordsStr === 'string')) return;
          const keywords: string[] = keywordsStr
            .split(',')
            .map(x => x.trim())
            .filter(x => x.length > 0);
          if (keywords == null || keywords.length === 0) return;
          const listAbcObs: Observable<ActionBarConfiguration[]>[] = [];
          keywords.forEach((keyword: string) => {
            listAbcObs.push(this.cacheService.getActionBarConfigurationsForObjectType(keyword));
          });

          observableCombineLatest(listAbcObs)
            .pipe(
              catchError((error, caught) => this.handleError(error, caught)),
              first(),
            )
            .subscribe((abcs: ActionBarConfiguration[][]) => {
              this.listActionBarConfigs = [].concat.apply([], abcs);
            });
        });
    }
  }

  actionBarClick(parameters: {
    conf: ActionBarConfiguration;
    selectedObjectIds?: string | string[];
  }) {
    let {conf, selectedObjectIds} = parameters;
    if (conf.sitetype === 'objectlist') conf.sitetype = 'olist';
    if (['get', 'timeline'].indexOf(conf.sitetype) > -1) {
      if (conf.urlid) {
        this._parent.onAddView.emit(new AddViewEvent(conf.sitetype, conf.urlid));
      } else {
        this._parent.onAddView.emit(new AddViewEvent(conf.sitetype, conf.linkedobject));
      }
    } else if (['list', 'olist', 'external'].indexOf(conf.sitetype) > -1) {
      this._parent.onAddView.emit(new AddViewEvent(conf.sitetype, conf.urlid));
    } else if (conf.sitetype === 'create') {
      this.cacheService
        .getDialogConfigurationByID(conf.linkedobject)
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe((dgConf: DialogConfiguration) => {
          this._parent.onAddView.emit(new AddViewEvent('create', dgConf.urlid));
        });
    } else if (conf.sitetype === 'wizard') {
      this.cacheService
        .getDialogConfigurationByID(conf.linkedobject)
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe((dgConf: DialogConfiguration) => {
          this._parent.onAddView.emit(new AddViewEvent('wizard', dgConf.urlid, selectedObjectIds));
        });
    } else if (conf.sitetype === 'batch') {
      this.cacheService
        .getDialogConfigurationByID(conf.linkedobject)
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe((dgConf: DialogConfiguration) => {
          this._parent.onAddView.emit(new AddViewEvent('batch', dgConf.urlid, selectedObjectIds));
        });
    } else if (conf.sitetype === 'batchlist') {
      this.cacheService
        .getDialogConfigurationByID(conf.linkedobject)
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe((dgConf: DialogConfiguration) => {
          this._parent.onAddView.emit(
            new AddViewEvent('batchlist', dgConf.urlid, selectedObjectIds),
          );
        });
    } else {
      let command: any[] = [''];
      if (conf.sitetype != null) command.push(conf.sitetype);
      if (conf.urlid != null) command.push(conf.urlid);
      if (conf.linkedobject != null) command.push(conf.linkedobject);
      this.router.navigate(command);
    }
  }

  private onManageClick(): void {
    this.resObjTypeObs
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        first(),
      )
      .subscribe((resultObjectType: string) => {
        if (resultObjectType == null) {
          resultObjectType = this.envConfig.getObjectTypeName('Resource');
        }
        if (this._config.propertyExists('UsageKeywords')) {
          this.manageWithUsageKeywords(resultObjectType);
        } else if (this._config.propertyExists('Filter')) {
          this.manageWithFilter(resultObjectType);
        }
      });
  }

  private manageWithUsageKeywords(resultObjectType: string): void {
    this._config
      .getPropertyValues('UsageKeywords')
      .pipe(
        map((strVal: string) => strVal.split(',')),
        catchError((error, caught) => this.handleError(error, caught)),
        first(),
      )
      .subscribe((keywords: string[]) => {
        if (keywords == null || keywords.length === 0) {
          this.openManageDialog(observableOf(null), resultObjectType);
          return;
        }

        const sscsObs: Observable<SearchScopeConfiguration[]>[] = [];
        keywords.forEach((keyword: string) => {
          sscsObs.push(this.cacheService.getSearchScopeConfigurationForObjectType(keyword));
        });

        const sscsObsJoined = observableForkJoin(sscsObs).pipe(
          map((sscss: SearchScopeConfiguration[][]) => {
            return [].concat.apply([], sscss);
          }),
          map((searchscopes: SearchScopeConfiguration[]) => {
            let newscopes = [];
            searchscopes.forEach((searchscope: SearchScopeConfiguration) => {
              let scope: SearchScopeConfiguration = {
                id: searchscope.id,
                displayname: searchscope.displayname,
                description: searchscope.description,
                usagekeyword: searchscope.usagekeyword,
                order: searchscope.order,
                attributesearched: searchscope.attributesearched,
                xpath: searchscope.xpath,
                resultobjecttype: searchscope.resultobjecttype,
                attributedisplayed: searchscope.attributedisplayed,
                sortingattribute: searchscope.sortingattribute,
                sortdirection: searchscope.sortdirection,
              };

              if (scope.xpath.includes('%ObjectID%')) {
                if (this._objectID !== null) {
                  scope.xpath = scope.xpath.replace(/%ObjectID%/g, this._objectID);
                }
              }
              newscopes.push(scope);
            });
            return newscopes;
          }),
        );

        this.openManageDialog(sscsObsJoined, resultObjectType);
      });
  }

  private manageWithFilter(resultObjectType: string): void {
    this._config
      .getPropertyValues('Filter')
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        first(),
      )
      .subscribe((filter: string) => {
        if (filter == null || filter.length === 0) {
          this.openManageDialog(observableOf(null), resultObjectType);
          return;
        }

        const ssc: SearchScopeConfiguration = <SearchScopeConfiguration>{
          id: null,
          displayname: null,
          xpath: filter,
          resultobjecttype: resultObjectType,
          attributedisplayed: null,
        };

        if (this._config.propertyExists('MainSearchScreenText')) {
          this._config
            .getPropertyValues('MainSearchScreenText')
            .pipe(
              catchError((error, caught) => this.handleError(error, caught)),
              first(),
            )
            .subscribe((searchText: string) => {
              ssc.displayname = searchText;
              this.openManageDialog(observableOf([ssc]), resultObjectType);
            });
        } else {
          this.openManageDialog(observableOf([ssc]), resultObjectType);
        }
      });
  }

  private openManageDialog(
    searchScopesObs: Observable<SearchScopeConfiguration[]>,
    resultObjectType: string,
  ): void {
    const attributesToSearchObs = this._config.getPropertyValues('AttributesToSearch');
    const languageResObs = this.cacheService.getLanguageResource();
    const addAllowedObs = this._config.AddPermission;
    const removeAllowedObs = this._config.RemovePermission;

    observableCombineLatest(
      searchScopesObs,
      this.sourceVoObs ? this.sourceVoObs : observableOf(null),
      this.columnsObs,
      attributesToSearchObs,
      languageResObs,
      addAllowedObs,
      removeAllowedObs,
      (
        sscs: SearchScopeConfiguration[],
        source: ValuesObject<any>,
        columnsStr: string,
        attributesToSearch: string,
        lr: any,
        addAllowed: boolean,
        removeAllowed: boolean,
      ) => {
        let columns: string[];
        if (columnsStr != null && columnsStr !== '') {
          columns = columnsStr.split(',').map(x => x.trim());
        } else {
          columns = [this.envConfig.getAttributeName('DisplayName')];
        }

        // /ResultObjectType[ObjectID=/ResourceObjectType[ObjectID='ResourceObjectID']/Attribute]
        let xpath: string = null;
        if (this.objectID != null && this.objectType != null) {
          xpath =
            '/' +
            resultObjectType +
            '[' +
            this.envConfig.getAttributeName('ObjectID') +
            '=/' +
            this.objectType +
            '[' +
            this.envConfig.getAttributeName('ObjectID') +
            "='" +
            this.objectID +
            "']/" +
            source.BindingPath +
            ']';
        }
        const ssc: SearchScopeConfiguration = <SearchScopeConfiguration>{
          id: null,
          displayname: LanguageService.translate('Search', 'Search', lr),
          xpath: xpath,
          resultobjecttype: resultObjectType,
          attributedisplayed: columns,
          attributesearched: attributesToSearch
            ? attributesToSearch.split(',')
            : [this.envConfig.getAttributeName('DisplayName')],
        };

        if (sscs != null && sscs.length === 1) {
          if (sscs[0].displayname == null) {
            sscs[0].displayname = LanguageService.translate('Search', 'Search', lr);
          }
          if (sscs[0].resultobjecttype == null) {
            sscs[0].resultobjecttype = resultObjectType;
          }
          if (sscs[0].attributedisplayed == null) {
            sscs[0].attributedisplayed = columns;
          }

          if (sscs[0].attributesearched == null) {
            sscs[0].attributesearched = attributesToSearch
              ? attributesToSearch.split(',')
              : [this.envConfig.getAttributeName('DisplayName')];
          }
        }

        if (sscs == null || sscs.length === 0) {
          addAllowed = false;
        }

        let dialogRef: MatDialogRef<MultivalueDialogComponent> =
          this.dialog.open(MultivalueDialogComponent);
        dialogRef.componentInstance.addAllowed = addAllowed === true;
        dialogRef.componentInstance.removeAllowed = removeAllowed === true;
        if (
          source.SpecialObject != null &&
          source.SpecialObject instanceof MultivalueDialogResult
        ) {
          dialogRef.componentInstance.setSelectedValues(
            source.SpecialObject.CurrentlySelected,
            source.SpecialObject.OriginalSelected,
          );
          dialogRef.componentInstance.setChanges(source.SpecialObject.Changes);
        } else {
          dialogRef.componentInstance.setSelectedValues(source.Values);
        }
        if (this._config.propertyExists('ListViewAddTitle')) {
          this._config
            .getPropertyValues('ListViewAddTitle')
            .pipe(
              catchError((error, caught) => this.handleError(error, caught)),
              first(),
            )
            .subscribe((title: string) => {
              dialogRef.componentInstance.addListTitle = title;
            });
        }
        if (this._config.propertyExists('SingleSelection')) {
          this._config
            .getBooleanPropertyValues('SingleSelection')
            .pipe(
              catchError((error, caught) => this.handleError(error, caught)),
              first(),
            )
            .subscribe((single: boolean) => {
              dialogRef.componentInstance.singleSelection = single;
            });
        }
        dialogRef.componentInstance.objectDisplayName = this.objectDisplayName;
        dialogRef.componentInstance.attributeLabel = this._config.Caption.pipe(
          map(vo => (vo ? vo.Values : null)),
        );
        dialogRef.componentInstance.SearchScopes = sscs;
        dialogRef.componentInstance.SelectedSearchScope = ssc;
        dialogRef
          .afterClosed()
          .pipe(
            catchError((error, caught) => this.handleError(error, caught)),
            first(),
          )
          .subscribe((result: MultivalueDialogResult) => {
            if (result != null) {
              this.formControl.setValue(result);
              this.formControl.markAsTouched();
              const addedGuids = result.Changes.filter(rm => rm.mark === SmdDataRowMark.Add).map(
                rm => rm.model[this.envConfig.getAttributeName('ObjectID')],
              );
              this.cacheService.addRecentGuids(addedGuids);
            }
          });
      },
    )
      .pipe(
        first(),
        catchError((error, caught) => this.handleError(error, caught)),
      )
      .subscribe();
  }

  refreshUI() {
    this.listDataTable.onResize();
  }
}

// ListFilter => XPath für anzuzeigende Elemente  // priority 1
// ODER
// TargetAttribute => Systemname von Attribut im aktuellen Objekt (SingleVal Identity feld oder MultiValue Idntity feld) // priority 2
// ColumnsToDisplay => Comma-separated List of Attributes
// beides nur verwendet wenn 'ShowSearchControl' == 'false'

// ResultObjectType =>

/*

/ResultObjectType[ObjectID=/ResourceObjectType[ObjectID='ResourceObjectID']/Attribute]

SelectedValue		-> valuesobject (bindingpath)	-> xpath
ListFilter			-> xpath
TargetAttribute		-> systemname	-> xpath

 */

// SelectedValue => Binding auf Array oder SingleValue [init damit, uncheck - remove value, check - add value]

// PageSize => default wert für paginator select

// UsageKeyword => comma separated list of keywords, searchscopes für die suche
// nur verwendet wenn 'ShowSearchControl' == 'true'
// suche nur wenn 'ShowSearchControl' == 'true'

// EmptyResultText => anzeigen wenn keine elemente im ergebnis

// ShowTitleBar => getLabel über Tabelle nochmal anzeigen

// ShowActionBar => wenn true, actionbar mit buttons anzeigen - AUSLASSEN

// EnableSelection => Checkboxen da oder nicht da

// SingleSelection => Nur eines selektierbar, default: multi select
